/**
 * DeviceHealthBadges — bateria + sinal estilo celular.
 *
 *  - mode='compact' (default): mostra agregado — pior bateria + pior sinal
 *    do conjunto. Tooltip lista cada device. Usado no card (espaço apertado).
 *
 *  - mode='expanded': um par de ícones por device. Usado no detalhe do ativo
 *    (espaço sobrando, mais granular).
 *
 *  Cores por threshold (verde/amarelo/vermelho).
 *  Tooltip custom ao hover (sem depender do title nativo do browser).
 */

import {
  Battery,
  BatteryFull,
  BatteryLow,
  BatteryMedium,
  BatteryWarning,
  Signal,
  SignalHigh,
  SignalLow,
  SignalMedium,
  SignalZero,
  WifiOff,
} from "lucide-react";
import clsx from "clsx";

import type { Device, DeviceStatus } from "@/types";
import { dbmToPercent, lqiToPercent } from "./HealthIndicators";

interface Item {
  device: Device;
  status: DeviceStatus | null;
}

interface Props {
  items: Item[];
  /** 'compact' (default) = ícones agregados; 'expanded' = um par por device. */
  mode?: "compact" | "expanded";
  className?: string;
}

export function DeviceHealthBadges({ items, mode = "compact", className }: Props) {
  // 2 filtros de "esconder do painel de saúde":
  //   - device.active === false → foi desinstalado; não é dado atual
  //   - status.is_stale === true → sem reading há muito tempo (2h+); valor velho
  // Ambos evitam mostrar "82% bateria" que é fantasma.
  const visible = items.filter(({ device, status }) => {
    if (device.active === false) return false;
    if (status?.is_stale) return false;
    return (
      status &&
      (status.battery_pct !== null ||
        status.signal_dbm !== null ||
        status.signal_lqi !== null ||
        !status.online)
    );
  });

  if (visible.length === 0) return null;

  if (mode === "expanded") {
    return <ExpandedBadges items={visible} className={className} />;
  }
  return <CompactBadges items={visible} className={className} />;
}

// ---------------------------------------------------------------------------
// Compact — agregado (pior bateria + pior sinal) + tooltip listando todos
// ---------------------------------------------------------------------------

function CompactBadges({ items, className }: { items: Item[]; className?: string }) {
  const offline = items.filter((i) => i.status && !i.status.online);
  const online  = items.filter((i) => i.status && i.status.online);

  // Pior bateria do conjunto
  const batteries = online
    .map((i) => i.status!.battery_pct)
    .filter((b): b is number => b !== null && b !== undefined);
  const worstBattery = batteries.length > 0 ? Math.min(...batteries) : null;

  // Pior sinal — separa por kind (wifi vs zigbee) e pega o pior em proporção
  const signals = online.flatMap((i) => {
    const dbm = i.status!.signal_dbm;
    const lqi = i.status!.signal_lqi;
    const out: { kind: "wifi" | "zigbee"; raw: number; pct: number; device: Device }[] = [];
    if (dbm != null) out.push({ kind: "wifi",   raw: dbm, pct: dbmToPercent(dbm), device: i.device });
    if (lqi != null) out.push({ kind: "zigbee", raw: lqi, pct: lqiToPercent(lqi), device: i.device });
    return out;
  });
  const worstSignal = signals.length > 0
    ? signals.reduce((a, b) => (a.pct < b.pct ? a : b))
    : null;

  // Tooltip detalhado — uma linha por device
  const detailLines: string[] = [];
  for (const { device, status } of items) {
    const parts: string[] = [device.name];
    if (status && !status.online) {
      parts.push("offline");
    } else if (status) {
      if (status.battery_pct != null) parts.push(`bat ${status.battery_pct}%`);
      if (status.signal_dbm  != null) parts.push(`${status.signal_dbm} dBm`);
      if (status.signal_lqi  != null) parts.push(`LQI ${status.signal_lqi}`);
    }
    detailLines.push(parts.join(" · "));
  }
  const tooltipDetail = detailLines.join("\n");

  return (
    <div className={clsx("flex items-center gap-1.5", className)}>
      {offline.length > 0 && (
        <Tooltip label={`${offline.length} device(s) offline\n\n${tooltipDetail}`}>
          <span className="flex items-center gap-1">
            <WifiOff className="h-4 w-4 text-red-500" />
            {offline.length > 1 && (
              <span className="text-[10px] font-semibold text-red-600 tabular-nums">
                {offline.length}
              </span>
            )}
          </span>
        </Tooltip>
      )}
      {worstBattery !== null && (
        <Tooltip label={`Bateria mínima: ${worstBattery}%\n\n${tooltipDetail}`}>
          {(() => {
            const { Icon, color } = batteryStyle(worstBattery);
            return <Icon className={clsx("h-4 w-4", color)} />;
          })()}
        </Tooltip>
      )}
      {worstSignal && (
        <Tooltip label={`Sinal mínimo: ${worstSignal.pct}%\n\n${tooltipDetail}`}>
          {(() => {
            const { Icon, color } = signalStyle(worstSignal.kind, worstSignal.raw);
            return <Icon className={clsx("h-4 w-4", color)} />;
          })()}
        </Tooltip>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Expanded — um par de ícones por device (usado no detalhe)
// ---------------------------------------------------------------------------

function ExpandedBadges({ items, className }: { items: Item[]; className?: string }) {
  return (
    <div className={clsx("flex items-center gap-3", className)}>
      {items.map(({ device, status }) => {
        const offline = status && !status.online;
        return (
          <div key={device.id} className="flex items-center gap-1.5">
            {offline && (
              <Tooltip label={`${device.name}\nOffline`}>
                <WifiOff className="h-4 w-4 text-red-500" />
              </Tooltip>
            )}
            {!offline && status?.battery_pct != null && (
              <Tooltip label={`${device.name}\nBateria: ${status.battery_pct}%`}>
                {(() => {
                  const { Icon, color } = batteryStyle(status.battery_pct);
                  return <Icon className={clsx("h-4 w-4", color)} />;
                })()}
              </Tooltip>
            )}
            {!offline && status?.signal_dbm != null && (
              <Tooltip
                label={`${device.name}\nSinal: ${status.signal_dbm} dBm · ${dbmToPercent(status.signal_dbm)}%`}
              >
                {(() => {
                  const { Icon, color } = signalStyle("wifi", status.signal_dbm);
                  return <Icon className={clsx("h-4 w-4", color)} />;
                })()}
              </Tooltip>
            )}
            {!offline && status?.signal_lqi != null && (
              <Tooltip
                label={`${device.name}\nSinal: LQI ${status.signal_lqi} · ${lqiToPercent(status.signal_lqi)}%`}
              >
                {(() => {
                  const { Icon, color } = signalStyle("zigbee", status.signal_lqi);
                  return <Icon className={clsx("h-4 w-4", color)} />;
                })()}
              </Tooltip>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tooltip custom (Tailwind group hover, sem deps)
// ---------------------------------------------------------------------------

function Tooltip({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <span className="group relative inline-flex">
      {children}
      <span
        className={clsx(
          "pointer-events-none absolute bottom-full left-1/2 z-20 mb-1.5 -translate-x-1/2",
          "whitespace-pre-line rounded-md bg-slate-900 px-2 py-1 text-[11px] font-medium text-white shadow-lg",
          "opacity-0 transition-opacity duration-100 group-hover:opacity-100",
        )}
      >
        {label}
      </span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Style helpers
// ---------------------------------------------------------------------------

function batteryStyle(pct: number) {
  if (pct <= 10)  return { Icon: BatteryWarning, color: "text-red-500" };
  if (pct <= 20)  return { Icon: BatteryLow,     color: "text-amber-500" };
  if (pct <= 50)  return { Icon: BatteryMedium,  color: "text-slate-500 dark:text-slate-400" };
  if (pct <= 80)  return { Icon: BatteryFull,    color: "text-slate-500 dark:text-slate-400" };
  return            { Icon: BatteryFull,    color: "text-emerald-500" };
}

function signalStyle(kind: "wifi" | "zigbee", value: number) {
  if (kind === "wifi") {
    if (value > -60) return { Icon: SignalHigh,   color: "text-emerald-500" };
    if (value > -70) return { Icon: SignalHigh,   color: "text-emerald-500" };
    if (value > -80) return { Icon: SignalMedium, color: "text-slate-500 dark:text-slate-400" };
    if (value > -88) return { Icon: SignalLow,    color: "text-amber-500" };
    return            { Icon: SignalZero,   color: "text-red-500" };
  }
  if (value > 200) return { Icon: SignalHigh,   color: "text-emerald-500" };
  if (value > 150) return { Icon: SignalHigh,   color: "text-emerald-500" };
  if (value > 100) return { Icon: SignalMedium, color: "text-slate-500 dark:text-slate-400" };
  if (value > 50)  return { Icon: SignalLow,    color: "text-amber-500" };
  return             { Icon: SignalZero,   color: "text-red-500" };
}

// Re-exports neutros pra compat
export const BatteryUnknown = Battery;
export const SignalUnknown = Signal;
